import { Reveal } from "@/components/Reveal";
import { ArrowUpRight } from "lucide-react";


const products = [
  {
    name: "Agentica Docu Flow",
    tagline: "Intelligent Document Automation",
    desc: "Agentica Docu Flow uses AI to process, organize, and manage your documents—automating workflows and turning information into actionable insights.",
    img: "/docu-flow.webp",
    brand: true,
    status: "Available",
    url: "https://docuflow.agentica-sol.com/",
  },
  {
    name: "Agentica Video Analysis",
    tagline: "Agentic Video Intelligence",
    desc: "Our Agentic Video Analysis platform turns video into actionable insights—using AI to detect objects, analyze events, and automate responses for smarter monitoring and decision-making.",
    img: "/video-analysis.webp",
    brand: true,
    status: "Available",
    url: "https://videoflow.agentica-sol.com/",
  },
  {
    name: "Agentica Clinical Flow",
    tagline: "Agentic Medical Writing, Clinical Evidence & Regulatory Review",
    desc: "Generate, review, validate and govern clinical documents with evidence-grounded AI, controlled workflows and audit-ready traceability.",
    img: "/clinical-flow.webp",
    brand: true,
    fit: "contain",
    bg: "#eef6f6",
    status: "Available",
    url: "https://clinicalflow.agentica-sol.com/",
  },
];

export default function Products() {
  return (
    <section id="products" className="relative bg-[#050505] py-24 lg:py-32 border-t border-white/10" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
            <span className="overline text-[#8b7cff]/90">02 — Agentic Products</span>
          </div>
          <h2 className="font-display font-700 tracking-tighter leading-none text-white text-4xl sm:text-5xl max-w-3xl">
            Deploy-ready agents, engineered by us.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          {products.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1} className="group">
              <div
                className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors duration-500"
                data-testid={`product-card-${i}`}
              >
                {/* Treated, spotlit photography clipped in a frame (brand assets shown untreated) */}
                <a
                  href={p.url || "#contact"}
                  target={p.url ? "_blank" : undefined}
                  rel={p.url ? "noopener noreferrer" : undefined}
                  onClick={p.url ? undefined : (e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                  className={`block relative h-64 overflow-hidden ${p.brand ? "bg-[#f4f7ff]" : ""}`}
                  style={p.bg ? { backgroundColor: p.bg } : undefined}
                  data-testid={`product-banner-link-${i}`}
                  aria-label={p.url ? `Open ${p.name} website` : `Request ${p.name}`}
                >
                  <img
                    src={p.img}
                    alt={p.name}
                    className={p.brand
                      ? `h-full w-full ${p.fit === "contain" ? "object-contain" : "object-cover object-top"} group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out`
                      : "h-full w-full object-cover grayscale brightness-75 contrast-125 group-hover:scale-105 group-hover:grayscale-0 transition-[transform,filter] duration-[900ms] ease-out"}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-[#050505] ${p.brand ? "via-[#050505]/10" : "via-[#050505]/30"} to-transparent`} />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{ background: "radial-gradient(400px circle at 50% 60%, rgba(157,107,255,0.22), transparent 70%)" }} />
                  <span className="absolute top-5 left-5 overline text-white/80 bg-black/40 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5">
                    {p.status}
                  </span>
                </a>

                <div className="p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-2xl sm:text-3xl font-700 text-white tracking-tight">{p.name}</h3>
                      <p className="text-[#8b7cff]/90 text-sm mt-1">{p.tagline}</p>
                    </div>
                    <a
                      href={p.url || "#contact"}
                      target={p.url ? "_blank" : undefined}
                      rel={p.url ? "noopener noreferrer" : undefined}
                      onClick={p.url ? undefined : (e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                      className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-white group-hover:bg-white group-hover:text-void transition-colors duration-300"
                      data-testid={`product-cta-${i}`}
                      aria-label={p.url ? `Open ${p.name} website` : `Request ${p.name}`}
                    >
                      <ArrowUpRight className="h-5 w-5" />
                    </a>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mt-5 max-w-md">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
